import React from 'react';
import {
  Typography,
  Box,
  Paper,
  Avatar
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import RepeatIcon from '@mui/icons-material/Repeat';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CommentIcon from '@mui/icons-material/Comment';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { PlatformPostProps } from './types';
import { SocialMediaImage } from './MediaHandler';

// Format numbers for display (e.g., 1.2K instead of 1200)
export const formatNumber = (num?: number) => {
  if (num === undefined) return '0';
  if (num < 1000) return num.toString();
  if (num < 1000000) return `${(num / 1000).toFixed(1)}K`;
  return `${(num / 1000000).toFixed(1)}M`;
};

// Generate sender initials for avatar
export const getInitials = (name: string) => {
  if (!name) return '?';
  return name.split(' ')
    .map(word => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

// Custom bookmark icon component for Instagram
const BookmarkBorderIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
  </svg>
);

// TwitterPost and FacebookPost have been moved to CorePlatforms.tsx

export const InstagramPost: React.FC<PlatformPostProps> = ({ 
  content, 
  sender, 
  displayImageUrl, 
  altText, 
  replies7d, 
  likes7d 
}) => (
  <Paper elevation={0} sx={{ 
    bgcolor: (theme) => theme.palette.mode === 'dark' ? '#000000' : '#ffffff',
    borderRadius: 0,
    border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#262626' : '#dbdbdb'}`,
    maxWidth: '100%'
  }}>
    {/* Header */}
    <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
      <Avatar 
        sx={{ 
          width: 32, 
          height: 32, 
          border: '2px solid #c13584', 
          padding: '2px', 
          bgcolor: 'white' 
        }}
      >
        <Avatar 
          sx={{ 
            width: '100%', 
            height: '100%', 
            bgcolor: 'primary.main'
          }}
        >
          {getInitials(sender || '')}
        </Avatar>
      </Avatar>
      
      <Box sx={{ ml: 1 }}>
        <Typography variant="subtitle2" sx={{ 
          fontWeight: 'bold',
          color: (theme) => theme.palette.mode === 'dark' ? '#f5f5f5' : 'inherit'
        }}>
          {sender?.replace(/\s+/g, '').toLowerCase() || 'anonymous'}
        </Typography>
      </Box>
      
      <Box sx={{ ml: 'auto' }}>
        <MoreHorizIcon sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#8e8e8e' : 'inherit' }} />
      </Box>
    </Box>

    {/* Image - comes before text on Instagram */}
    {displayImageUrl && (
      <SocialMediaImage 
        src={displayImageUrl} 
        alt={altText}
        platform="instagram"
      />
    )}

    {/* Action buttons */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <FavoriteBorderIcon sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#f5f5f5' : 'inherit' }} />
        <CommentIcon sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#f5f5f5' : 'inherit' }} />
        <ShareOutlinedIcon sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#f5f5f5' : 'inherit' }} />
      </Box>
      <Box>
        <BookmarkBorderIcon />
      </Box>
    </Box>

    {/* Likes count */}
    <Box sx={{ px: 2, pb: 1 }}>
      <Typography variant="subtitle2" sx={{ 
        fontWeight: 'bold',
        color: (theme) => theme.palette.mode === 'dark' ? '#f5f5f5' : 'inherit'
      }}>
        {formatNumber(likes7d)} likes
      </Typography>
    </Box>

    {/* Content */}
    <Box sx={{ px: 2, pb: 2 }}>
      <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#f5f5f5' : 'inherit' }}>
        <Typography component="span" variant="subtitle2" sx={{ 
          fontWeight: 'bold', 
          mr: 1,
          color: (theme) => theme.palette.mode === 'dark' ? '#f5f5f5' : 'inherit'
        }}>
          {sender?.replace(/\s+/g, '').toLowerCase() || 'anonymous'}
        </Typography>
        {content}
      </Typography>
    </Box>

    {/* Comments count */}
    <Box sx={{ px: 2, pb: 2 }}>
      <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#8e8e8e' : theme.palette.text.secondary }}>
        View all {formatNumber(replies7d)} comments
      </Typography>
    </Box>
  </Paper>
);

export const LinkedInPost: React.FC<PlatformPostProps> = ({ 
  content, 
  sender, 
  displayImageUrl, 
  altText, 
  replies7d, 
  likes7d 
}) => (
  <Paper elevation={0} sx={{ 
    bgcolor: '#ffffff',
    borderRadius: 2,
    border: '1px solid #e0e0e0'
  }}>
    {/* Header */}
    <Box sx={{ display: 'flex', p: 2 }}>
      <Avatar sx={{ width: 48, height: 48, bgcolor: '#0077B5' }}>
        {getInitials(sender || '')}
      </Avatar>
      
      <Box sx={{ ml: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#000000DE' }}>
          {sender || 'Anonymous'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#00000099' }}>
          {sender?.includes('Company') ? 'Company' : 'Professional'} · 1d
        </Typography>
      </Box>
      
      <Box sx={{ ml: 'auto' }}>
        <MoreHorizIcon sx={{ color: '#00000099' }} />
      </Box>
    </Box>

    {/* Content */}
    <Box sx={{ px: 2, pb: 2 }}>
      <Typography 
        variant="body1" 
        sx={{ 
          whiteSpace: 'pre-wrap', 
          color: '#000000DE',
          fontSize: '0.95rem',
          lineHeight: 1.4
        }}
      >
        {content}
      </Typography>
    </Box>

    {/* Image */}
    {displayImageUrl && (
      <SocialMediaImage 
        src={displayImageUrl} 
        alt={altText}
        platform="linkedin"
      />
    )}

    {/* Reactions summary */}
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#38434f' : '#e0e0e0'}`,
      px: 2,
      py: 1
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Avatar sx={{ width: 16, height: 16, bgcolor: '#0077B5', fontSize: '10px' }}>👍</Avatar>
        <Typography variant="body2" sx={{ 
          color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#00000099',
          ml: 0.5 
        }}>
          {formatNumber(likes7d)}
        </Typography>
      </Box>
      
      <Box>
        <Typography variant="body2" sx={{ 
          color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#00000099' 
        }}>
          {formatNumber(replies7d)} comments
        </Typography>
      </Box>
    </Box>

    {/* Action buttons */}
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-around', 
      px: 1,
      py: 0.5
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        px: 1, 
        py: 1, 
        borderRadius: 1, 
        '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#f5f5f5' } 
      }}>
        <ThumbUpOutlinedIcon sx={{ 
          color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#00000099',
          fontSize: 20, 
          mr: 1 
        }} />
        <Typography variant="body2" sx={{ 
          color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#00000099',
          fontWeight: 'medium' 
        }}>
          Like
        </Typography>
      </Box>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        px: 1, 
        py: 1, 
        borderRadius: 1, 
        '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#f5f5f5' } 
      }}>
        <ChatBubbleOutlineIcon sx={{ 
          color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#00000099',
          fontSize: 20, 
          mr: 1 
        }} />
        <Typography variant="body2" sx={{ 
          color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#00000099',
          fontWeight: 'medium' 
        }}>
          Comment
        </Typography>
      </Box>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        px: 1, 
        py: 1, 
        borderRadius: 1, 
        '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#f5f5f5' } 
      }}>
        <RepeatIcon sx={{ 
          color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#00000099',
          fontSize: 20, 
          mr: 1 
        }} />
        <Typography variant="body2" sx={{ 
          color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#00000099',
          fontWeight: 'medium' 
        }}>
          Repost
        </Typography>
      </Box>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        px: 1, 
        py: 1, 
        borderRadius: 1, 
        '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#f5f5f5' } 
      }}>
        <ShareOutlinedIcon sx={{ 
          color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#00000099',
          fontSize: 20, 
          mr: 1 
        }} />
        <Typography variant="body2" sx={{ 
          color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#00000099',
          fontWeight: 'medium' 
        }}>
          Send
        </Typography>
      </Box>
    </Box>
  </Paper>
);

// Re-export SocialMediaImage so other components can import it from this file
export { SocialMediaImage };